import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Loader2, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  Video, 
  Brain, 
  Users, 
  Target,
  TrendingUp
} from 'lucide-react';
import { getAnalysisById } from '../services/api';

const ProcessingStatus = ({ analysisId, onComplete, onError }) => {
  const [status, setStatus] = useState('processing');
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('initializing');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(300); // 5 minutes default
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  // const [analysisData, setAnalysisData] = useState(null); // FIX: remove unused state

  // Processing stages with estimated durations
  const stages = useMemo(() => [
    { 
      key: 'initializing', 
      label: 'Initializing Analysis', 
      icon: Clock, 
      duration: 5,
      description: 'Setting up AI analysis pipeline'
    },
    { 
      key: 'video_processing', 
      label: 'Processing Video', 
      icon: Video, 
      duration: 60,
      description: 'Analyzing match footage with GPT Vision'
    },
    { 
      key: 'tactical_analysis', 
      label: 'Tactical Analysis', 
      icon: Brain, 
      duration: 90,
      description: 'Identifying weaknesses and strategies'
    },
    { 
      key: 'formation_detection', 
      label: 'Formation Detection', 
      icon: Users, 
      duration: 45,
      description: 'Detecting opponent formation patterns'
    },
    { 
      key: 'player_movement', 
      label: 'Player Movement Analysis', 
      icon: TrendingUp, 
      duration: 60,
      description: 'Tracking player positioning and movement'
    },
    { 
      key: 'generating_insights', 
      label: 'Generating Insights', 
      icon: Target, 
      duration: 40,
      description: 'Creating tactical recommendations'
    }
  ], []);

  // Calculate total expected duration
  const totalExpectedDuration = useMemo(
    () => stages.reduce((sum, stage) => sum + stage.duration, 0),
    [stages]
  );

  // Poll backend for analysis status
  const checkAnalysisStatus = useCallback(async () => {
    try {
      const response = await getAnalysisById(analysisId);

      if (response.success) {
        const data = response.analysis;

        // Check if analysis is complete
        if (data.status === 'completed') {
          setStatus('completed');
          setProgress(100);
          setCurrentStage('completed');
          // setAnalysisData(data); // Removed unused state
          
          if (onComplete) {
            onComplete(data);
          }
          return true; // Stop polling
        }

        // Check if analysis failed
        if (data.status === 'failed' || data.status === 'error') {
          setStatus('error');
          setError(data.error_message || 'Analysis failed. Please try again.');
          
          if (onError) {
            onError(data.error_message);
          }
          return true; // Stop polling
        }

        // Update progress based on current stage
        if (data.current_stage) {
          setCurrentStage(data.current_stage);
          
          // Calculate progress percentage
          const stageIndex = stages.findIndex(s => s.key === data.current_stage);
          if (stageIndex !== -1) {
            const completedDuration = stages
              .slice(0, stageIndex)
              .reduce((sum, stage) => sum + stage.duration, 0);
            
            const progressPercent = Math.min(
              95, // Cap at 95% until actually complete
              Math.round((completedDuration / totalExpectedDuration) * 100)
            );
            
            setProgress(progressPercent);
          }
        }

        // Update status
        setStatus('processing');
        return false; // Continue polling

      } else {
        throw new Error(response.message || 'Failed to fetch analysis status');
      }

    } catch (err) {
      console.error('Error checking analysis status:', err);
      
      // Don't immediately fail - might be temporary network issue
      // Only fail after multiple consecutive errors
      return false; // Continue polling
    }
  }, [analysisId, onComplete, onError, stages, totalExpectedDuration]); // FIX: removed setAnalysisData from deps

  // Start polling when component mounts
  useEffect(() => {
    if (!analysisId) return;

    let pollInterval;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;

    const poll = async () => {
      try {
        const shouldStop = await checkAnalysisStatus();
        
        if (shouldStop) {
          clearInterval(pollInterval);
        } else {
          consecutiveErrors = 0; // Reset error counter on success
        }
      } catch (err) {
        consecutiveErrors++;
        
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          setStatus('error');
          setError('Unable to connect to analysis service. Please check your connection.');
          clearInterval(pollInterval);
          
          if (onError) {
            onError('Connection lost');
          }
        }
      }
    };

    // Initial check
    poll();

    // Poll every 3 seconds
    pollInterval = setInterval(poll, 3000);

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [analysisId, checkAnalysisStatus, onError]);

  // Elapsed time counter
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Calculate estimated time remaining
  useEffect(() => {
    if (progress > 0 && elapsedTime > 0) {
      const estimatedTotal = (elapsedTime / progress) * 100;
      const remaining = Math.max(0, Math.round(estimatedTotal - elapsedTime));
      setEstimatedTimeRemaining(remaining);
    }
  }, [progress, elapsedTime]);

  // Format time in MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get current stage info
  const getCurrentStageInfo = () => {
    return stages.find(s => s.key === currentStage) || stages[0];
  };

  const stageInfo = getCurrentStageInfo();
  const StageIcon = stageInfo.icon;

  // Render different states
  if (status === 'error') {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Analysis Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (status === 'completed') {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4 animate-bounce">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Analysis Complete!</h2>
          <p className="text-gray-600 mb-2">
            Your tactical analysis is ready
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Completed in {formatTime(elapsedTime)}
          </p>
          
          <div className="inline-flex items-center space-x-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Processing time: {formatTime(elapsedTime)}</span>
          </div>
        </div>
      </div>
    );
  }

  // Processing state
  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Analyzing Match</h2>
        <p className="text-gray-600">
          AI is processing your video and generating tactical insights
        </p>
      </div>

      {/* Progress Circle */}
      <div className="flex justify-center mb-8">
        <div className="relative">
          <svg className="w-32 h-32 transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#E5E7EB"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#1E40AF"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - progress / 100)}`}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          </svg>
          
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{progress}%</div>
              <div className="text-xs text-gray-500">complete</div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Stage */}
      <div className="mb-8">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="bg-blue-100 p-3 rounded-full">
            <StageIcon className="h-6 w-6 text-blue-600 animate-pulse" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{stageInfo.label}</h3>
            <p className="text-sm text-gray-600">{stageInfo.description}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Time Stats */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-gray-50 p-4 rounded-lg text-center">
          <div className="text-sm text-gray-600 mb-1">Elapsed Time</div>
          <div className="text-xl font-semibold text-gray-900">
            {formatTime(elapsedTime)}
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg text-center">
          <div className="text-sm text-gray-600 mb-1">Est. Remaining</div>
          <div className="text-xl font-semibold text-gray-900">
            {formatTime(estimatedTimeRemaining)}
          </div>
        </div>
      </div>

      {/* Stage Timeline */}
      <div className="space-y-3">
        {stages.map((stage, index) => {
          const isCompleted = stages.findIndex(s => s.key === currentStage) > index;
          const isCurrent = stage.key === currentStage;
          const Icon = stage.icon;

          return (
            <div
              key={stage.key}
              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                isCurrent
                  ? 'bg-blue-50 border-2 border-blue-600'
                  : isCompleted
                  ? 'bg-green-50'
                  : 'bg-gray-50'
              }`}
            >
              <div
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  isCurrent
                    ? 'bg-blue-600'
                    : isCompleted
                    ? 'bg-green-600'
                    : 'bg-gray-300'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle className="h-5 w-5 text-white" />
                ) : isCurrent ? (
                  <Loader2 className="h-5 w-5 text-white animate-spin" />
                ) : (
                  <Icon className="h-5 w-5 text-white" />
                )}
              </div>
              
              <div className="flex-1">
                <div
                  className={`text-sm font-medium ${
                    isCurrent || isCompleted ? 'text-gray-900' : 'text-gray-500'
                  }`}
                >
                  {stage.label}
                </div>
              </div>
              
              {isCompleted && (
                <span className="text-xs text-green-600 font-medium">✓ Done</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Message */}
      <div className="mt-6 text-center text-sm text-gray-500">
        Please keep this page open while analysis is in progress
      </div>
    </div>
  );
};

export default ProcessingStatus;
